'use client'

import HotelMarker from '@/components/googleMap/markers/HotelMarker';
import Pin from '@/components/googleMap/markers/PinMarker';
import NoImage from '@/components/illustratores/NoImage';
import ImageLoading from '@/components/ImageLoading';
import CustomMarker from '@/components/leaflet/CustomMarker';
import LeafletMapContainer from '@/components/leaflet/LeafletMapContainer';
import { placeIcons } from '@/components/PlaceIcon';
import { useItineraryMapStore } from '@/core/stores/itinerary';
import { useItineraryStoreV2 } from '@/core/stores/itinerary/itineraryV2';
import useNextRouter from '@/hooks/useNextRouter';
import { ItineraryItemV2 } from '@/types/itinerary/itineraryV2';
import { generateColor } from '@/utils/generateColor';
import { faCamera } from '@fortawesome/pro-light-svg-icons';
import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePathname, useSearchParams } from 'next/navigation';
import ActivityPin from './components/PinMarker';
import { LeafLetZoomTo } from '@/components/leaflet/LeafLetZoomTo';
import LeafLetCluster from '@/components/leaflet/LeafLetCluster';
import { Fragment, useEffect, useState } from 'react';
import { Polyline, Tooltip } from 'react-leaflet';

const handleShowStars = (starsRate: number) => {
    let stars = [];
    for (var i = 0; i < Math.max(+starsRate, 5); i++) {
        stars.push(<FontAwesomeIcon icon={faStar} className="h-3 w-3 text-white" />)
    }
    return stars.map((item) => item)
}


export default function LeafLetMapLayer() {
    const { hotels, showHotels, activityModal, setActivityModal, setShowPlaceDetail, hotelDetail,
        suggestionActivities, modalData, setModalData,
        setUserPin, setHotelDetail,
        setIsLoadingPlace, routes } = useItineraryMapStore();

    const { itinerary, itineraryDays, baseUrl, activeDays, setZoomTo, zoomTo } = useItineraryStoreV2()

    const pathname = usePathname()
    const router = useNextRouter()
    const searchParams = useSearchParams();

    const lat = itinerary?.cityLat || searchParams.get('lat') || 48.8575475
    const lng = itinerary?.cityLng || searchParams.get('lng') || 2.3513765

    return (
        <LeafletMapContainer key={`${lat}${lng}`} center={[+lat, +lng]}>
            <LeafLetZoomTo zoomToLat={zoomTo?.lat} zoomToLng={zoomTo?.lng} />
            {routes.map(({ index, route }) => activeDays.includes(index) && (
                <Polyline
                    key={index}
                    positions={route}
                    pathOptions={{
                        color: generateColor(index + 1),
                        weight: 5,
                        opacity: 0.8,
                        dashArray: '10,6',
                        lineCap: 'round',
                        lineJoin: 'round',

                    }}
                >
                    <Tooltip sticky>{`Day ${index + 1} Route`}</Tooltip>
                </Polyline>
            ))}
            {itineraryDays?.map((day, index) => {
                if (activeDays.includes(index)) {

                    const routeLatLngs = day.items
                        .map(item => [Number(item.lat), Number(item.lng)] as [number, number]);

                    return <Fragment key={index}>
                        {day.items.map((item, indexItem) => <CustomMarker
                            tooltip={
                                item?.images && item?.images[0] && <div
                                    className='transition-all z-50 duration-300  w-max  rounded-2xl
                                max-w-40  origin-bottom   bg-white p-1 flex flex-col'>

                                    <div className='relative'>

                                        {item?.rate && <span className='absolute top-2 z-30 left-2 rounded-full text-xs bg-yellow-400 text-white px-1 py-0.5 mb-2 font-semibold '>{item?.rate}</span>}
                                        <div className='relative rounded-2xl overflow-hidden border'>
                                            <p className='text-xs py-2 px-1 rounded-xl text-center bg-gradient-to-t from-black/80 to-black/0
                                                         absolute -bottom-1 w-full block text-wrap max-w-full   left-1/2 z-20 text-white -translate-x-1/2 mb-1
                                                         font-semibold '>
                                                <p className='line-clamp-2'>
                                                    {item?.name}
                                                </p>
                                            </p>

                                            {item?.images[0] ? (
                                                <ImageLoading
                                                    width={250}
                                                    height={250}
                                                    src={item?.images[0]}
                                                    alt="room"
                                                    className="aspect-square h-full w-full rounded-l-2xl object-cover lg:rounded-2xl xl:h-full"
                                                />
                                            ) : (
                                                <div className="flex  aspect-[1.5/1] h-full w-full items-center justify-center rounded-l-2xl bg-gray-200 object-cover text-gray-400 lg:aspect-[1.3/1] lg:rounded-2xl xl:h-full xl:w-full">
                                                    {/* <IconPhoto className=" h-16" /> */}
                                                    <NoImage />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            }


                            onClick={() => {
                                if (pathname !== `${baseUrl}/days/${index + 1}`) {
                                    router.push(`${baseUrl}/days/${index + 1}`)
                                }

                                setZoomTo({ lat: +item.lat, lng: +item.lng })
                                const itemWithDay: any = item
                                itemWithDay.day = index + 1
                                setActivityModal(null)
                                setModalData(itemWithDay)
                            }}
                            key={item.id}
                            position={[+item.lat, +item.lng]}
                        >
                            <Pin
                                index={indexItem}
                                isActive={item.id === (modalData as ItineraryItemV2)?.id}
                                icon={<FontAwesomeIcon icon={faCamera} size='xl' className="w-5 h-5" />}
                                color={generateColor(index + 1)}
                                place={item}
                            />
                        </CustomMarker>)}
                    </Fragment>
                }
            })}

            {(showHotels && hotels.length > 0) &&
                <LeafLetCluster
                    color='#f75751'
                >
                    {hotels.map(item => <CustomMarker
                        key={item._id}
                        position={[Number(item.lat), Number(item.lng)]}
                        onClick={() => {
                            setHotelDetail(hotels.find(hotel => hotel.id === item.id) || null)
                            setActivityModal(null)
                            if (`${baseUrl}/hotels` !== pathname) {
                                router.push(`${baseUrl}/hotels`)
                            }
                        }}
                        tooltip={item.name && typeof item.name === 'string' && <div
                            className=' w-max  rounded-2xl
                     max-w-40  bg-white p-1.5 flex flex-col'>
                            <p className='text-sm mb-1 font-semibold line-clamp-2'>{item.name}</p>

                            <div className='relative'>
                                <span className='absolute top-2 left-2 rounded-full text-xs flex items-center gap-0.5 bg-yellow-400 text-white px-1 py-0.5 mb-2 font-semibold '>
                                    {handleShowStars(+item?.stars)}
                                </span>
                                {item.images && item.images[0]?.url ? (
                                    <ImageLoading
                                        width={150}
                                        height={150}
                                        quality={60}
                                        src={`${item.images[0]?.url}`}
                                        alt="room"
                                        className="aspect-square h-full w-full rounded-2xl object-cover lg:rounded-2xl xl:h-full"
                                    />
                                ) : (
                                    <div className="flex  aspect-[1.5/1] h-full w-full items-center justify-center rounded-2xl bg-gray-200 object-cover text-gray-400 lg:aspect-[1.3/1] lg:rounded-2xl xl:h-full xl:w-full">
                                        <NoImage />
                                    </div>
                                )}
                            </div>
                        </div>}
                    >
                        <HotelMarker
                            isActive={!!hotelDetail && (hotelDetail.id === item.id)}
                            icon={placeIcons.Hotel}
                            color={'#f75751'}
                            place={item}
                        />
                    </CustomMarker>)}
                </LeafLetCluster>}
            {
                suggestionActivities && suggestionActivities.length > 0 &&
                <LeafLetCluster color='#7e3af2'>
                    {suggestionActivities.map(item =>
                        <CustomMarker
                            key={item.productCode}
                            position={[Number(item?.lat), Number(item?.lng)]}
                            onClick={() => {
                                setModalData(null)
                                setActivityModal(item)
                            }}
                            tooltip={
                                item?.images && item?.images[0] && <div
                                    className='w-max  rounded-2xl relative
                                     max-w-40 origin-bottom overflow-hidden  shadow bg-white p-1 flex flex-col'
                                >

                                    <div className='absolute h-1/2 font-semibold text-center bottom-0 left-0 p-2 py-3  z-20 w-full  
                                bg-gradient-to-t text-wrap from-gray-800 to-gray-800/0 flex flex-col justify-end
                                 rounded-t-md text-white  '>
                                        <p className='line-clamp-2'>

                                            {item.title}
                                        </p>
                                    </div>

                                    <div className='relative z-10'>

                                        {item.fromPrice &&
                                            <span className='absolute top-2 z-30 right-2 rounded-full text-xs
                               bg-secondary-500 text-white px-1 py-0.5 mb-2 font-semibold '
                                            >
                                                $ {item?.fromPrice}
                                            </span>}

                                        {!!item?.rate && <span className='absolute top-2 z-30 left-2 rounded-full text-xs bg-yellow-400 text-white px-1 py-0.5 mb-2 font-semibold '>{item?.rate}</span>}
                                        <div className='relative rounded-2xl overflow-hidden border'>

                                            {item?.images[0] ? (
                                                <ImageLoading
                                                    width={250}
                                                    height={250}
                                                    src={item?.images[0]}
                                                    alt="room"
                                                    className="aspect-square h-full w-full rounded-l-2xl object-cover lg:rounded-2xl xl:h-full"
                                                />
                                            ) : (
                                                <div className="flex  aspect-[1.5/1] h-full w-full items-center justify-center rounded-l-2xl bg-gray-200 object-cover text-gray-400 lg:aspect-[1.3/1] lg:rounded-2xl xl:h-full xl:w-full">
                                                    {/* <IconPhoto className=" h-16" /> */}
                                                    <NoImage />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            }
                        >
                            <ActivityPin

                                isActive={activityModal?.productCode === item.productCode}
                                icon={<FontAwesomeIcon icon={faCamera} size='xl' className="w-5 h-5" />}
                                color={'#7e3af2'}
                                place={item}
                            />
                        </CustomMarker>
                    )}
                </LeafLetCluster>
            }
        </LeafletMapContainer >
    );
}